/**
 * Custom hooks for equipment state management
 * 
 * These hooks provide convenient access to equipment state and actions.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useEquipmentContext } from '../contexts/equipment-context';
import {
  equipmentService,
  eventsService,
  measurementsService,
  kpiService
} from '../services/equipment';
import {
  EquipmentNode,
  TechnologyType,
  DateRange
} from '../services/equipment/types';
import { getEquipmentNodeUrl, getEquipmentStatusUrl } from '../services/navigation';
import { 
  validateAndSanitizeSite, 
  isSiteChanged 
} from '../utils/site-validator';

// Helper function to find a node by ID in the hierarchy
const findNodeById = (node: EquipmentNode, id: string): EquipmentNode | null => {
  if (node.id === id) {
    return node;
  }

  if (node.children) {
    for (const child of node.children) {
      const found = findNodeById(child, id);
      if (found) {
        return found;
      }
    }
  }

  return null;
};

/**
 * Hook for equipment selection and navigation
 */
export const useEquipmentSelection = () => {
  const { state, dispatch } = useEquipmentContext();

  // Select a node in the equipment hierarchy
  const selectNode = useCallback((node: EquipmentNode | null, updateUrl = true) => {
    if (!node) {
      return;
    }
    console.log(`useEquipmentSelection - selectNode ${node.type} ${node.id}`);
    dispatch({ type: 'SET_SELECTED_NODE', payload: node });

    // Update URL if requested and we have the necessary context
    if (updateUrl && state.site) {
      if (node.type === 'asset') {
        const newUrl = getEquipmentNodeUrl(state.site, node.id, node.id);
        window.history.replaceState(null, '', newUrl);
      } else if (node.type === 'position') {
        const apmId = node.id.split('#')[0]
        const newUrl = getEquipmentNodeUrl(state.site, apmId, node.id);
        window.history.replaceState(null, '', newUrl);
      }
    }

    // if (node.type === 'position') {
      // const nodeId = node.id.split('#')[1]
      // dispatch({ type: 'SET_POSITION', payload: nodeId });
      // dispatch({ type: 'SET_SELECTED_NODE_ID', payload: node.id });
      // dispatch({ type: 'SET_SELECTED_NODE', payload: node });
    // } else if (node.type === 'asset') {
      // dispatch({ type: 'SET_POSITION', payload: null });
      // dispatch({ type: 'SET_SITE_APM_ID', payload: { site: state.site!, apmId: node.id } });
      // dispatch({ type: 'SET_SELECTED_NODE_ID', payload: node.id });
      // dispatch({ type: 'SET_SELECTED_NODE', payload: node });
    // }

    // if (node.type === 'asset' || node.type === 'position') {
    //   dispatch({ type: 'SET_SELECTED_NODE_ID', payload: node.id });
    // } else if (node.type === 'system') {
    //   dispatch({ type: 'SET_SITE_APM_ID', payload: { site: state.site!, apmId: node.id } });
    //   dispatch({ type: 'SET_SELECTED_NODE_ID', payload: null });
    //   dispatch({ type: 'SET_SELECTED_NODE', payload: null });
    // }

  }, [dispatch, state.site]);

  const selectNodeById = useCallback((nodeId: string | null, updateUrl = false) => {
    if (!nodeId || !state.equipmentHierarchy || !state.equipmentHierarchy.rootNode) {
      console.log('selectNodeById: Cannot select node - missing hierarchy or nodeId');
      return;
    }
    const node = findNodeById(state.equipmentHierarchy.rootNode, nodeId);
    if (!node) {
      return;
    }
    dispatch({ type: 'SET_SELECTED_NODE', payload: node });

    // Update URL if requested and we have the necessary context
    if (updateUrl && state.site) {
      if (node.type === 'asset') {
        const newUrl = getEquipmentNodeUrl(state.site, node.id, node.id);
        window.history.replaceState(null, '', newUrl);
      } else if (node.type === 'position') {
        const apmId = node.id.split('#')[0]
        const newUrl = getEquipmentNodeUrl(state.site, apmId, node.id);
        window.history.replaceState(null, '', newUrl);
      }
    }

    // if (node.type === 'position') {
    //   const nodeId = node.id.split('#')[1]
    //   dispatch({ type: 'SET_POSITION', payload: nodeId });
    //   dispatch({ type: 'SET_SELECTED_NODE_ID', payload: node.id });
    //   dispatch({ type: 'SET_SELECTED_NODE', payload: node });
    // } else if (node.type === 'asset') {
    //   dispatch({ type: 'SET_POSITION', payload: null });
    //   dispatch({ type: 'SET_SITE_APM_ID', payload: { site: state.site!, apmId: node.id } });
    //   dispatch({ type: 'SET_SELECTED_NODE_ID', payload: node.id });
    //   dispatch({ type: 'SET_SELECTED_NODE', payload: node });
    // }

    // if (node.type === 'asset' || node.type === 'position') {
    //   dispatch({ type: 'SET_SELECTED_NODE_ID', payload: node.id });
    // } else if (node.type === 'system') {
    //   dispatch({ type: 'SET_SITE_APM_ID', payload: { site: state.site!, apmId: node.id } });
    //   dispatch({ type: 'SET_SELECTED_NODE_ID', payload: null });
    //   dispatch({ type: 'SET_SELECTED_NODE', payload: null });
    // }

  }, [dispatch, state.site]);

  // Get the currently selected node from the hierarchy
  const selectedNode = useMemo(() => {
    if (!state.equipmentHierarchy || !state.selectedNodeId) {
      return null;
    }
    return findNodeById(state.equipmentHierarchy.rootNode, state.selectedNodeId);
  }, [state.equipmentHierarchy, state.selectedNodeId]);

  return {
    selectedNodeId: state.selectedNodeId,
    selectedNode,
    selectNode,
    selectNodeById,
  };
};

/**
 * Hook for equipment data loading
 */
export const useEquipmentData = () => {
  const { state, dispatch } = useEquipmentContext();

  // Load equipment hierarchy
  const loadEquipmentHierarchy = useCallback(async (site?: string) => {
    const targetSite = site || state.site;

    if (!targetSite) {
      console.log('loadEquipmentHierarchy: Missing site');
      return;
    }

    console.log(`loadEquipmentHierarchy: Loading for site=${targetSite}`);

    dispatch({
      type: 'SET_LOADING',
      payload: { key: 'hierarchy', value: true }
    });
    dispatch({
      type: 'SET_ERROR',
      payload: { key: 'hierarchy', value: null }
    });

    try {
      const hierarchy = await equipmentService.getFullEquipmentTree(targetSite);

      dispatch({ type: 'SET_EQUIPMENT_HIERARCHY', payload: hierarchy });
      console.log('loadEquipmentHierarchy: Successfully loaded hierarchy');
      
      // After hierarchy loads successfully, trigger dependent data loading
      // This ensures all data is refreshed for the new site
      if (site && site !== state.site) {
        console.log('loadEquipmentHierarchy: Site changed, will reload dependent data after apmId is set');
      }
    } catch (error) {
      console.error('loadEquipmentHierarchy: Error loading hierarchy', error);
      dispatch({
        type: 'SET_ERROR',
        payload: {
          key: 'hierarchy',
          value: error instanceof Error ? error.message : 'Failed to load equipment hierarchy'
        }
      });
    } finally {
      dispatch({
        type: 'SET_LOADING',
        payload: { key: 'hierarchy', value: false }
      });
    }
  }, [dispatch, state.site]);

  // Load events for selected equipment
  const loadEvents = useCallback(async () => {
    if (!state.site || !state.apmId) {
      console.log('loadEvents: Missing site or apmId');
      return;
    }

    // Don't load if hierarchy is still loading
    if (state.isLoading.hierarchy) {
      console.log('loadEvents: Skipping - hierarchy still loading');
      return;
    }

    dispatch({
      type: 'SET_LOADING',
      payload: { key: 'events', value: true }
    });
    dispatch({
      type: 'SET_ERROR',
      payload: { key: 'events', value: null }
    });

    try {
      // Pass date range to the service for server-side filtering
      const events = await eventsService.getWorkOrderEvents(
        state.site,
        state.apmId,
        state.dateRange
      );
      dispatch({ type: 'SET_EVENTS', payload: events });
    } catch (error) {
      dispatch({
        type: 'SET_ERROR',
        payload: {
          key: 'events',
          value: error instanceof Error ? error.message : 'Failed to load events'
        }
      });
    } finally {
      dispatch({
        type: 'SET_LOADING',
        payload: { key: 'events', value: false }
      });
    }
  }, [state.site, state.selectedNodeId, state.apmId, state.dateRange, state.isLoading.hierarchy, dispatch]);

  // Load trend chart data for selected equipment
  const loadTrendChartData = useCallback(async () => {
    console.log(`[use-equipment-state] loadTrendChartData selectedNodeId ${state.selectedNodeId}`);
    if (!state.site || !state.apmId || !state.selectedNodeId || !state.selectedNode || state.selectedNode.type !== "position") {
      console.log('loadTrendChartData: Missing required data or node is not a position');
      return;
    }

    // Don't load if hierarchy is still loading
    if (state.isLoading.hierarchy) {
      console.log('loadTrendChartData: Skipping - hierarchy still loading');
      return;
    }

    dispatch({
      type: 'SET_LOADING',
      payload: { key: 'trendCharts', value: true }
    });
    dispatch({
      type: 'SET_ERROR',
      payload: { key: 'trendCharts', value: null }
    });

    try {
      // Only load data for selected technologies
      const techList: TechnologyType[] = ['monitron', 'pulse', 'cadet', 'thermal'];
      const promises = techList.map(async (tech) => {
        try {
          const data = await measurementsService.getTrendChartData(
            state.site!,
            state.apmId!,
            state.selectedNodeId!,
            tech,
            state.dateRange
          );

          dispatch({
            type: 'SET_TREND_CHART_DATA',
            payload: { technology: tech, data }
          });
        } catch (error) {
          console.error(`Error loading ${tech} data:`, error);
          dispatch({
            type: 'SET_TREND_CHART_DATA',
            payload: { technology: tech, data: null }
          });
        }
      });

      await Promise.all(promises);
    } catch (error) {
      dispatch({
        type: 'SET_ERROR',
        payload: {
          key: 'trendCharts',
          value: error instanceof Error ? error.message : 'Failed to load trend chart data'
        }
      });
    } finally {
      dispatch({
        type: 'SET_LOADING',
        payload: { key: 'trendCharts', value: false }
      });
    }
  }, [state.site, state.selectedNodeId, state.apmId, state.dateRange, state.selectedNode, state.isLoading.hierarchy, dispatch]);

  // Load KPI data for site and APM ID
  const loadKPIData = useCallback(async () => {
    if (!state.site || !state.apmId) {
      console.log('loadKPIData: Missing site or apmId');
      return;
    }

    // Don't load if hierarchy is still loading
    if (state.isLoading.hierarchy) {
      console.log('loadKPIData: Skipping - hierarchy still loading');
      return;
    }

    dispatch({
      type: 'SET_LOADING',
      payload: { key: 'kpi', value: true }
    });
    dispatch({
      type: 'SET_ERROR',
      payload: { key: 'kpi', value: null }
    });

    try {
      const data = await kpiService.getKPIData(
        state.site,
        state.apmId,
        state.dateRange
      );

      dispatch({ type: 'SET_KPI_DATA', payload: data });
    } catch (error) {
      dispatch({
        type: 'SET_ERROR',
        payload: {
          key: 'kpi',
          value: error instanceof Error ? error.message : 'Failed to load KPI data'
        }
      });
    } finally {
      dispatch({
        type: 'SET_LOADING',
        payload: { key: 'kpi', value: false }
      });
    }
  }, [state.site, state.apmId, state.isLoading.hierarchy, state.dateRange, dispatch]);

  // Initialize site and APM ID
  const initializeSiteAndApmId = useCallback((site: string, apmId: string) => {
    dispatch({
      type: 'SET_SITE_APM_ID',
      payload: { site, apmId }
    });
  }, [dispatch]);

  // Effect to automatically reload dependent data when site and apmId are available
  useEffect(() => {
    if (state.site && state.apmId && state.equipmentHierarchy && !state.isLoading.hierarchy) {
      console.log('useEquipmentData: Site and apmId available, loading dependent data');
      
      // Load all dependent data in parallel
      // These functions handle their own loading states
      Promise.all([
        loadEvents(),
        loadKPIData()
      ]).catch(error => {
        console.error('useEquipmentData: Error loading dependent data after site change', error);
      });
    }
  }, [state.site, state.apmId, state.equipmentHierarchy, state.isLoading.hierarchy, loadEvents, loadKPIData]);

  // Effect to reload trend chart data when selected node changes (after site change)
  useEffect(() => {
    if (state.site && state.apmId && state.selectedNodeId && state.equipmentHierarchy && !state.isLoading.hierarchy) {
      console.log('useEquipmentData: Selected node available, loading trend chart data');
      loadTrendChartData().catch(error => {
        console.error('useEquipmentData: Error loading trend chart data after site change', error);
      });
    }
  }, [state.site, state.apmId, state.selectedNodeId, state.equipmentHierarchy, state.isLoading.hierarchy, loadTrendChartData]);

  // Effect to manage loading states during site transitions
  // This ensures proper loading indicators are shown during the complete site change workflow
  useEffect(() => {
    if (state.siteChangeInProgress) {
      console.log('useEquipmentData: Site change in progress, managing loading states');
      
      // When site change starts, we expect hierarchy to be loading
      // Other loading states will be managed by their respective functions
      if (!state.isLoading.hierarchy) {
        console.log('useEquipmentData: Site change in progress but hierarchy not loading - this may indicate an issue');
      }
    }
  }, [state.siteChangeInProgress, state.isLoading.hierarchy]);

  // Utility function to check if we're in a complete site transition
  const isSiteTransitionInProgress = useCallback(() => {
    return state.siteChangeInProgress || 
           (state.site && !state.apmId && state.isLoading.hierarchy) ||
           (state.site && state.apmId && !state.equipmentHierarchy);
  }, [state.siteChangeInProgress, state.site, state.apmId, state.isLoading.hierarchy, state.equipmentHierarchy]);

  return {
    equipmentHierarchy: state.equipmentHierarchy,
    events: state.events,
    trendChartData: state.trendChartData,
    kpiData: state.kpiData,
    isLoading: state.isLoading,
    errors: state.errors,
    loadEquipmentHierarchy,
    loadEvents,
    loadTrendChartData,
    loadKPIData,
    initializeSiteAndApmId,
    isSiteTransitionInProgress
  };
};

/**
 * Hook for site change functionality
 * Provides debounced site change logic with input disabling during process
 */
export const useSiteChange = () => {
  const { state, dispatch } = useEquipmentContext();
  const { loadEquipmentHierarchy } = useEquipmentData();
  
  // Refs for managing debouncing
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingSiteRef = useRef<string | null>(null);

  // Cleanup function for clearing pending debounced operations
  const cleanup = useCallback(() => {
    // Clear debounce timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }

    // Clear pending site
    pendingSiteRef.current = null;
  }, []);

  // Perform the actual site change
  const performSiteChange = useCallback(async (newSite: string) => {
    const { sanitizedSite, validation } = validateAndSanitizeSite(newSite);

    // Validate site format
    if (!validation.isValid) {
      dispatch({
        type: 'SET_SITE_CHANGE_ERROR',
        payload: validation.error || 'Invalid site format'
      });
      return;
    }

    // Check if site is actually different
    if (!isSiteChanged(state.site || '', sanitizedSite)) {
      console.log('useSiteChange: Site unchanged, skipping reload');
      return;
    }

    // Set loading state (this will disable the input)
    dispatch({ type: 'SET_SITE_CHANGE_LOADING', payload: true });

    try {
      console.log(`useSiteChange: Starting site change to ${sanitizedSite}`);

      // Dispatch site change action (this will reset the state and clear all data)
      // This action clears: apmId, hierarchy, selectedNode, events, trends, KPI, expanded nodes
      dispatch({
        type: 'CHANGE_SITE',
        payload: { site: sanitizedSite }
      });

      // Load equipment hierarchy for the new site
      // The useEquipmentData effects will automatically handle loading dependent data
      // once the hierarchy is loaded and apmId is set (via node selection)
      await loadEquipmentHierarchy(sanitizedSite);

      // Clear any previous errors and show success on successful change
      dispatch({ type: 'SET_SITE_CHANGE_ERROR', payload: null });
      dispatch({ type: 'SET_SITE_CHANGE_SUCCESS', payload: true });

      // Auto-clear success message after 3 seconds
      setTimeout(() => {
        dispatch({ type: 'SET_SITE_CHANGE_SUCCESS', payload: false });
      }, 3000);

      console.log(`useSiteChange: Successfully changed site to ${sanitizedSite}`);
      console.log('useSiteChange: Dependent data will be loaded automatically when apmId is set');
    } catch (error) {
      console.error('useSiteChange: Error changing site', error);
      dispatch({
        type: 'SET_SITE_CHANGE_ERROR',
        payload: error instanceof Error ? error.message : 'Failed to change site'
      });
    } finally {
      // Clear loading state (this will re-enable the input)
      dispatch({ type: 'SET_SITE_CHANGE_LOADING', payload: false });
    }
  }, [state.site, dispatch, loadEquipmentHierarchy]);

  // Debounced site change function
  const changeSite = useCallback((newSite: string) => {
    // Don't allow new site changes if one is already in progress
    if (state.siteChangeInProgress) {
      console.log('useSiteChange: Site change already in progress, ignoring new request');
      return;
    }

    // Clear any pending debounced operations
    cleanup();

    // Store the pending site
    pendingSiteRef.current = newSite;

    // Set up debounced execution (500ms delay)
    debounceTimeoutRef.current = setTimeout(() => {
      const siteToProcess = pendingSiteRef.current;
      if (siteToProcess !== null) {
        performSiteChange(siteToProcess);
        pendingSiteRef.current = null;
      }
    }, 500);
  }, [state.siteChangeInProgress, cleanup, performSiteChange]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    // State
    currentSite: state.site,
    siteChangeInProgress: state.siteChangeInProgress,
    siteChangeError: state.siteChangeError,
    siteChangeSuccess: state.siteChangeSuccess,
    
    // Actions
    changeSite,
    
    // Utilities
    validateSite: (site: string) => validateAndSanitizeSite(site).validation,
    sanitizeSite: (site: string) => validateAndSanitizeSite(site).sanitizedSite
  };
};

/**
 * Hook for equipment state initialization
 */
export const useEquipmentInitialization = (site: string, apmId: string, selectedNodeId?: string) => {
  const { initializeSiteAndApmId, loadEquipmentHierarchy, equipmentHierarchy } = useEquipmentData();
  const { selectNodeById } = useEquipmentSelection();
  const initializationRef = useRef<{ site?: string; apmId?: string; selectedNodeId?: string }>({});
  const hierarchyLoadedRef = useRef(false);

  // Initialize site and APM ID - only when they change
  useEffect(() => {
    if (initializationRef.current.site !== site || initializationRef.current.apmId !== apmId) {
      console.log(`useEquipmentInitialization: Initializing site=${site}, apmId=${apmId}`);
      initializeSiteAndApmId(site, apmId);
      initializationRef.current.site = site;
      initializationRef.current.apmId = apmId;
      hierarchyLoadedRef.current = false;
    }
  }, [site, apmId, initializeSiteAndApmId]);

  // Load equipment hierarchy - only after site is set and when it changes
  useEffect(() => {
    if (!hierarchyLoadedRef.current &&
      initializationRef.current.site === site) {
      console.log(`useEquipmentInitialization: Loading hierarchy for site=${site}`);
      loadEquipmentHierarchy(site);
      hierarchyLoadedRef.current = true;
    }
  }, [site, loadEquipmentHierarchy]);

  // Set selected node - only after hierarchy is loaded and when selectedNodeId changes
  useEffect(() => {
    if (!selectedNodeId) {
      selectedNodeId = apmId;
    }
    if (selectedNodeId &&
      equipmentHierarchy &&
      initializationRef.current.selectedNodeId !== selectedNodeId) {
      console.log(`useEquipmentInitialization: Selecting node ${selectedNodeId}`);
      selectNodeById(selectedNodeId, false); // Add false to disable URL updates during init
      initializationRef.current.selectedNodeId = selectedNodeId;
    }
  }, [selectedNodeId, equipmentHierarchy, selectNodeById]);
};